import { clsx } from "clsx";
import Konva from "konva";
import { debounce } from "lodash-es";
import { nanoid } from "nanoid";
import { mix } from "polished";
import { equals } from "ramda";
import { type FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { useHover } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { normalize } from "widgets/utils";

import { useTrackTooltip } from "../../../app-utils/hooks/useTrackTooltip.js";
import { rangeNormalizedLength } from "../../../app-utils/range.js";
import { TRACK_TOOLTIP_TYPE } from "../../../app-utils/track-shapes/shape-feature.js";
import {
  ArrowDirection,
  drawMessageOnTrack,
  type GenomeViewerDefaultShape,
  type GenomeViewerShape,
  type GenomeViewerShapeInteractivity,
  getShapeRenderer,
} from "../../../app-utils/track-shapes/track-shapes-konva-renderers.js";
import {
  type BamSummary,
  type Contig,
  type PileupAlignment,
  type PileupAlignmentSegment,
  type PileupElement,
  type Range,
  type TrackNgsConfiguration,
  type TrackNgsOrderedBamFilesData,
} from "../../../graphql.js";
import { fetchNgsPileupFeatures, fetchNgsReadTooltip, type ITrackData } from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { CANVAS_TRACK_MARGIN } from "../../../settings/genome-viewer-settings.js";
import {
  GENOME_TRACK_COLOR,
  NGS_READS_PILEUP_COLORING,
  NGS_READS_PILEUP_DENSITY,
  SEQ_RESIDUE_NU_COLORS,
  TRACK_TYPE,
} from "../../../settings/settings.js";
import { type CallTooltip } from "../features-track/FeatureTrack.js";
import { type SetTooltipContent } from "../GenomeViewerTracksList.js";
import styleTrack from "../styles/Track.module.scss";
import { DEFAULT_NGS_LANES_COUNT, NGS_LANES_GAP, NGS_READS_SHAPE_HEIGHT } from "./_mod.js";
import { NgsReadTrackHeader } from "./NGSReadsTrackHeader.js";

// TODO Optimizations for data retrieving and caching
//  - Create a LRU Cache for requests (but only locally and maybe stringify/parse in localStorage) to improve zoom performance.
//  - The data should still be updated after the data fetch (and drawn?). Currently the next zoom level always erase previous data.
//  - Create some kind of optimization when zoom ranges collide by some units. If not => delete cache.

// TODO Optimizations for rendering (We should do the analysis first and the drawing after @see renderElement())
//  - Offscreen Canvas Rendering API (the whole canvas + data) => transfer redux changes via worker messages
//  - Do shape processing within a worker
//  - Do data processing in a Web Assembly module
//  - If the amount of children per layers is huge, process to a scale first (the cleaning takes too much time and looks broken)

// FIXME The scrollbar push the genome browser (currently "fixed" with absolute positioning).
//       The Viewer should have its own ResizeObserver.

type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any ? A : never;

interface INgsReadTracksProps {
  trackId: string;
  trackHeight: number; // TODO Delete once TrackHeader is flexible (flexbox/grid).

  // trackId should be enough but the API is not ready for that. Should refactor all tracks.
  // @see setTrackData(), fetchNgsPileupFeatures() and how trackId is defined in the parent component
  trackName: string;
  bamFileId: number;

  setTooltipContent: SetTooltipContent;
}

/// Cigar docs ///
// @see https://jef.works/blog/2017/03/28/CIGAR-strings-for-dummies/
// @see https://www.drive5.com/usearch/manual/cigar.html
// @see https://bioinformatics.cvr.ac.uk/java-cigar-parser-for-sam-format/
// @see http://samtools.github.io/hts-specs/SAMv1.pdf
/*
M	 Match           Can be either an alignment match or mismatch. The nucleotide is present in the reference.
N	 Skipped region  A region of nucleotides is not present in the read (Thick line)
X	 Read Mismatch   The nucleotide is present in the reference (Show sequence)
I	 Insertion       The nucleotide is present in the read  but not in the reference. (Green?)
D	 Deletion        The nucleotide is present in the reference but not in the read (Red?)
S	 Soft Clipping   The clipped nucleotides are present in the read
                   Note: Should not push the position to draw nucleotides. This is information for the next CIGAR action.
H	 Hard Clipping   The clipped nucleotides are not present in the read.
                   Note: Unlike "S", we have to push the position but show missing nucleotides somehow.
                   Shape may look like Soft Clipping but without the letter.
P	 Padding         Padded area in the read and not in the reference
=	 Read Match      The nucleotide is present in the reference (also "EQ")
*/
// FLAGS: @see https://broadinstitute.github.io/picard/explain-flags.html (pdf page 7)

// Is exported with memo(). See bellow.
const NgsReadTrack: FC<INgsReadTracksProps> = ({ trackId, ...props }) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const ref = useRef<HTMLDivElement>(null);

  const { hoverProps, isHovered } = useHover({});

  const [ngsPileupData, setNgsPileupData] = useState([]);
  const [isRendering, setRendering] = useState(false);
  const bamFileSummary = useSelector<IAppState, BamSummary>(
    (state) => state.genomeBrowser.bamDatafiles[props.bamFileId],
    shallowEqual,
  );

  // -- Customization: In the Track Header (+ Redux)
  const ngsConfiguration = useSelector<IAppState, TrackNgsConfiguration>((state) => {
    const track = state.genomeBrowser.tracks.find((tr) => tr.type === TRACK_TYPE.NGS);
    return track?.trackCustomization?.ngsConfiguration;
  });
  const bamFilesConfig: TrackNgsOrderedBamFilesData = useMemo(
    () => ngsConfiguration?.orderedBamFiles?.find((bamFileData) => bamFileData.bamFileId === props.bamFileId),
    [ngsConfiguration],
  );
  const lanesCount = bamFilesConfig.pileupLanesCount || DEFAULT_NGS_LANES_COUNT;
  const includeSoftClippedReads = bamFilesConfig.pileupIncludeSoftClippedReads || false;
  const density =
    (bamFilesConfig.pileupDensity as keyof typeof NGS_READS_PILEUP_DENSITY) || NGS_READS_PILEUP_DENSITY.MEDIUM;
  const coloringMode =
    (bamFilesConfig.pileupColoring as keyof typeof NGS_READS_PILEUP_COLORING) || NGS_READS_PILEUP_COLORING.DEFAULT;
  const shapeHeight: number = NGS_READS_SHAPE_HEIGHT[density];

  // -- Tooltip Request
  const loadNgsReadTooltip = (alignment: PileupAlignment, segment: PileupAlignmentSegment, index: number) => {
    return dispatch(fetchNgsReadTooltip(props.trackName, contig, props.bamFileId, alignment, segment, index));
  };

  // -- Request
  const loadNgsPileUpData = useRef(
    debounce((...args: ArgumentTypes<typeof fetchNgsPileupFeatures>) => {
      dispatch(fetchNgsPileupFeatures(...args)).then((data) => {
        const dontDraw = data === null || (Array.isArray(data) && data.length === 0);
        if (dontDraw === false) {
          setRendering(true);
        }
        setNgsPileupData(data);
      });
    }, 500),
  );

  // -- Redux data
  const contig = useSelector<IAppState, Contig>((state) => state.genomeBrowser.contig);
  const range = useSelector<IAppState, Range>((state) => state.genomeBrowser.range, shallowEqual);
  const trackWidth = useSelector<IAppState, number>((state) => state.genomeBrowser.layout.trackWidth, shallowEqual);
  const { isLoading, range: dataRange } = useSelector<IAppState, ITrackData>(
    (state) => state.genomeBrowser.trackDataMap[trackId],
    equals,
  );

  // null: failed to load (Memory overflow), false | undefined = no data
  const showPileup = useMemo(
    () => (ngsPileupData === null ? null : Array.isArray(ngsPileupData) && ngsPileupData.length > 0),
    [ngsPileupData],
  );

  // We need to enlarge by 1 unit because the range describes nucleotide number and not the axis value.
  const normalizedHorizontal = useMemo(() => rangeNormalizedLength(Math.ceil)(trackWidth)(range), [trackWidth, range]);

  // -- Render
  const height = props.trackHeight + CANVAS_TRACK_MARGIN * 2; //useMemo(() => ngsPileupData?.length * (shapeHeight + 4) - 4 ?? 0, [ngsPileupData, shapeHeight]); // rows gap = 2 (default)

  // NGS Layer + "no-letter" clipping (soft and hard)
  const ngsLayer = useMemo(() => new Konva.Layer({ clearBeforeDraw: true, y: CANVAS_TRACK_MARGIN }), []);
  const callTooltip = useTrackTooltip(ngsLayer, props.setTooltipContent);
  const interactionLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: false, y: CANVAS_TRACK_MARGIN }),
    [],
  );
  const { renderArrow, renderLine } = useMemo(() => {
    // For instance, to see the "first" nucleotide, we need to start from "zero", not "1".
    return getShapeRenderer({
      layer: ngsLayer,
      interactionLayer,
      normalize: normalizedHorizontal,
      visibleRange: range,
    });
  }, [normalizedHorizontal, ngsLayer, shapeHeight, range]);

  // Deletions and Mismatches Layer
  const nucleotideLayer = useMemo(() => new Konva.Layer({ clearBeforeDraw: true, y: CANVAS_TRACK_MARGIN }), []);
  const { renderArrow: renderNucArrow } = useMemo(() => {
    // For instance, to see the "first" nucleotide, we need to start from "zero", not "1".
    return getShapeRenderer({
      layer: nucleotideLayer,
      interactionLayer,
      normalize: normalizedHorizontal,
      visibleRange: range,
    });
  }, [normalizedHorizontal, nucleotideLayer, shapeHeight, range]);

  // Insertions Layer
  const insertionsLayer = useMemo(() => new Konva.Layer({ clearBeforeDraw: true, y: CANVAS_TRACK_MARGIN }), []);
  const { renderInsertion } = useMemo(() => {
    // For instance, to see the "first" nucleotide, we need to start from "zero", not "1".
    return getShapeRenderer({
      layer: insertionsLayer,
      interactionLayer,
      normalize: normalizedHorizontal,
      visibleRange: range,
    });
  }, [normalizedHorizontal, nucleotideLayer, shapeHeight, range]);

  const drawLetterShape = (position: number, y: number, letter: string, fill: string): GenomeViewerDefaultShape => {
    const nucArrowShape = renderNucArrow({
      y,
      start: position,
      stop: position + 1,
      direction: ArrowDirection.None,
      fill,
      height: shapeHeight,
    });
    nucArrowShape.text(letter);
    return nucArrowShape;
  };

  const textLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: false, y: CANVAS_TRACK_MARGIN }),
    [],
  );
  const drawTrackMessage = drawMessageOnTrack(textLayer);

  // On mount + Cleanup
  useEffect(() => {
    // We can have access to the stage from the layer. No need to keep it globally.
    const stage = new Konva.Stage({ container: ref.current, width: trackWidth, height });
    stage.add(ngsLayer);
    stage.add(nucleotideLayer);
    stage.add(insertionsLayer);
    stage.add(interactionLayer);
    stage.add(textLayer);

    return function cleanup() {
      stage.destroy();
    };
  }, []);

  // load again, request
  useEffect(() => {
    loadNgsPileUpData.current(props.trackName, contig, range, props.bamFileId, lanesCount, includeSoftClippedReads);
  }, [range, lanesCount, includeSoftClippedReads]);

  // Update text layer FIRST (isLoading, range too wide...)
  useEffect(() => {
    const stage = textLayer.getStage();
    if (stage == undefined) {
      return; // probably no canvas yet
    }

    stage.width(trackWidth);

    textLayer.destroyChildren();
    textLayer.draw();

    if (isLoading) {
      /* do nothing: There is a global loading spinner now */
      // drawTrackMessage("Loading...", !showPileup, true);
    } else if (isRendering) {
      drawTrackMessage("Rendering...", { stickToTop: !showPileup, withOverlay: true });
    } else if (showPileup === null) {
      // When null, an error occurred in the MT. Very probably a memory overflow
      // (too many data because the range is too wide)
      ngsLayer.destroyChildren();
      ngsLayer.clearCache();
      ngsLayer.draw();
      nucleotideLayer.destroyChildren();
      nucleotideLayer.clearCache();
      nucleotideLayer.draw();
      insertionsLayer.destroyChildren();
      insertionsLayer.clearCache();
      insertionsLayer.draw();
      interactionLayer.destroyChildren();
      interactionLayer.draw();
      drawTrackMessage(`Zoom in${lanesCount > 10 ? " or decrease lanes quantity" : ""} to see read data.`, {
        resetHeight: true,
      });
    } else if (!showPileup) {
      ngsLayer.destroyChildren();
      ngsLayer.clearCache();
      ngsLayer.draw();
      nucleotideLayer.destroyChildren();
      nucleotideLayer.clearCache();
      nucleotideLayer.draw();
      insertionsLayer.destroyChildren();
      insertionsLayer.clearCache();
      insertionsLayer.draw();
      interactionLayer.destroyChildren();
      interactionLayer.draw();
      drawTrackMessage("There are no reads to display.", { resetHeight: true });
    }
  }, [isLoading, isRendering, showPileup, trackWidth]);

  // Drag effect
  useEffect(() => {
    // Hide tooltip DOM element on drag
    //setTooltipStyle.current({ left: -10000, top: -10000, width: 0, height: 0 });

    if (range && dataRange) {
      // const previousRangeLength = previousRange.current.stop - previousRange.current.start;
      const previousRangeLength = dataRange.stop - dataRange.start;
      const currentRangeLength = range.stop - range.start;

      if (previousRangeLength === currentRangeLength) {
        // This is a drag event, so we move the layer's offset instead of drawing everything.
        const [, , diff] = normalizedHorizontal(dataRange.start, range.start);
        ngsLayer.x(-diff);
        ngsLayer.batchDraw(); // limit framerate

        nucleotideLayer.x(-diff);
        nucleotideLayer.batchDraw(); // limit framerate

        insertionsLayer.x(-diff);
        insertionsLayer.batchDraw(); // limit framerate
      }
    }

    ngsLayer.listening(false);
    nucleotideLayer.listening(false);
    insertionsLayer.listening(false);

    interactionLayer.destroyChildren();
    interactionLayer.batchDraw(); // limit framerate
  }, [range]);

  // Render again on data change
  useEffect(() => {
    const stage = ngsLayer.getStage();
    if (stage == undefined) return; // probably no canvas yet
    if (dataRange == undefined || range.start !== dataRange.start || range.stop !== dataRange.stop) return; // too early to draw

    // Clean layer.
    interactionLayer.destroyChildren();

    // Remove cache and set default position because of drag event
    ngsLayer.destroyChildren();
    ngsLayer.clearCache();
    ngsLayer.x(0);
    ngsLayer.listening(true);

    nucleotideLayer.destroyChildren();
    nucleotideLayer.clearCache();
    nucleotideLayer.x(0);
    nucleotideLayer.listening(true);

    insertionsLayer.destroyChildren();
    insertionsLayer.clearCache();
    insertionsLayer.x(0);
    insertionsLayer.listening(true);

    stage.width(trackWidth);

    // Prepare track (don't draw)
    if (showPileup) {
      // Prepare every pileup rows
      ngsPileupData.map((row, index) => {
        row.pileupElements?.map((element) => {
          renderElement(
            element,
            index * (shapeHeight + NGS_LANES_GAP[density]),
            shapeHeight,
            range,
            {
              renderLine,
              renderArrow,
              renderInsertion,
              drawLetterShape,
              //setTooltipStyle: setTooltipStyle.current,
              //setShowTooltip,
              loadNgsReadTooltip,
              callTooltip,
            },
            coloringMode,
            bamFileSummary,
          );
        });
      });
    } else {
      setRendering(false);
    }

    // Draw on next tick
    requestAnimationFrame(() => {
      // We limit the offscreen drawing of insertions, deletions and mismatches to 1/3 of the current range
      const [, , range2ThirdWidth] = normalizedHorizontal(
        0,
        Math.min(Math.ceil((range.stop - range.start) * (2 / 3)), 5000), // Currently 5000 is setup in the server side.
      );
      ngsLayer.cache({ x: -range2ThirdWidth, y: 0, width: trackWidth + range2ThirdWidth * 2, height });
      ngsLayer.draw();
      nucleotideLayer.cache({ x: -range2ThirdWidth, y: 0, width: trackWidth + range2ThirdWidth * 2, height });
      nucleotideLayer.draw();
      insertionsLayer.cache({ x: -range2ThirdWidth, y: 0, width: trackWidth + range2ThirdWidth * 2, height });
      insertionsLayer.draw();
      stage.height(height);
      setRendering(false);
    });
  }, [trackWidth, ngsPileupData, shapeHeight, coloringMode, bamFileSummary]);

  return (
    <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
      <NgsReadTrackHeader bamFileId={props.bamFileId} isTrackHovered={isHovered} />
      <div className={clsx(styleTrack.container, styleTrack.container_no_bottom, styleTrack.container_no_top)}>
        <div ref={ref} />
      </div>
    </div>
  );
};

// TODO Can be improved if treated as a hook?
interface INgsDrawOperations {
  renderLine: ReturnType<typeof getShapeRenderer>["renderLine"];
  renderArrow: ReturnType<typeof getShapeRenderer>["renderArrow"];
  renderInsertion: ReturnType<typeof getShapeRenderer>["renderInsertion"];
  drawLetterShape: (position: number, y: number, letter: string, fill: string) => any;
  callTooltip: CallTooltip;
  loadNgsReadTooltip: (alignment: PileupAlignment, segment: PileupAlignmentSegment, index: number) => Promise<any>;
}

const renderElement = (
  element: PileupElement,
  y: number,
  height: number,
  range: Range,
  drawOps: INgsDrawOperations,
  coloringMode: string,
  bamFileSummary: BamSummary,
) => {
  const { first, second } = element;
  const { renderLine, renderArrow, renderInsertion, drawLetterShape, loadNgsReadTooltip, callTooltip } = drawOps;

  const firstElementShape: GenomeViewerShapeInteractivity = first != undefined && drawElement(true, first);
  const secondElementShape: GenomeViewerShapeInteractivity = second != undefined && drawElement(false, second);
  if (firstElementShape && secondElementShape) {
    firstElementShape.interactiveShape.on("mouseenter", () => secondElementShape.doInteract.show(true));
    firstElementShape.interactiveShape.on("mouseleave", secondElementShape.doInteract.hide);
    secondElementShape.interactiveShape.on("mouseenter", () => firstElementShape.doInteract.show(true));
    secondElementShape.interactiveShape.on("mouseleave", firstElementShape.doInteract.hide);
  }

  function drawElement(isFirst: boolean, element: PileupAlignment): GenomeViewerShapeInteractivity {
    if (!Array.isArray(element.segments)) return;

    let segmentShapeInteractivity: GenomeViewerShapeInteractivity;
    const { segments } = element;

    // Used to detect if some segments stop and start are at the same position
    let lastStop = -1;

    const drawSegment = (segment: PileupAlignmentSegment, segmentIndex: number) => {
      let arrowDirection = ArrowDirection.None;

      if (element.strand === "+") {
        // First part
        if (segmentIndex === segments.length - 1) {
          // Last segment of the first mate
          arrowDirection = ArrowDirection.Right;
        }
      } else if (element.strand === "-") {
        // Second part
        if (segmentIndex === 0) {
          // First segment of the second mate
          arrowDirection = ArrowDirection.Left;
        }
      }

      //
      // color determination code for the arrow elements, depending on selected coloring mode
      //
      let fill: string;
      let hasStroke = false; // in some coloring modes the stroke is not required or leads to ugly display when zoomed out
      switch (coloringMode) {
        case NGS_READS_PILEUP_COLORING.DEFAULT:
          fill = element.singleton // mate unmapped
            ? GENOME_TRACK_COLOR.SINGLE
            : !element.interChromosomal
              ? GENOME_TRACK_COLOR.DEFAULT // mate is on same chromosome
              : GENOME_TRACK_COLOR.MATE_DIFF_CHR; // mate is on different chromosome
          hasStroke = true;
          break;
        case NGS_READS_PILEUP_COLORING.STRAND:
          fill = element.strand === "-" ? GENOME_TRACK_COLOR.STRAND_MINUS : GENOME_TRACK_COLOR.STRAND_PLUS;
          break;
        case NGS_READS_PILEUP_COLORING.PE_STRAND:
          fill = element.templateStrand === "-" ? GENOME_TRACK_COLOR.STRAND_MINUS : GENOME_TRACK_COLOR.STRAND_PLUS;
          break;
        case NGS_READS_PILEUP_COLORING.MAPPING_QUALITY:
          // the min/max quality values are well defined (0..255) and can be hard-coded here,
          // but reflecting some extreme coloring as it is done in Processor
          const qualityMin = 4;
          const qualityMax = 254;
          if (element.mappingQuality >= qualityMin && element.mappingQuality <= qualityMax) {
            const qualityMixValue: number = normalize(
              Math.max(qualityMin, Math.min(qualityMax, element.mappingQuality)),
              qualityMin,
              qualityMax,
              0,
              1,
            );
            fill = mix(qualityMixValue, GENOME_TRACK_COLOR.GRADIENT_STOP, GENOME_TRACK_COLOR.GRADIENT_START);
          } else if (element.mappingQuality < qualityMin) {
            fill = GENOME_TRACK_COLOR.LOW_MAPPING_QUALITY;
          } else if (element.mappingQuality > qualityMax) {
            fill = GENOME_TRACK_COLOR.HIGH_MAPPING_QUALITY;
          }
          break;
        case NGS_READS_PILEUP_COLORING.SOFT_CLIPPING:
          fill = segment.softClipped ? GENOME_TRACK_COLOR.STRAND_MINUS : GENOME_TRACK_COLOR.CLIPPING;
          break;
        case NGS_READS_PILEUP_COLORING.SPLIT_READS:
          fill = element.split ? GENOME_TRACK_COLOR.STRAND_MINUS : GENOME_TRACK_COLOR.CLIPPING;
          break;
        case NGS_READS_PILEUP_COLORING.READ_LENGTH:
          const extendedFileMin: number = bamFileSummary?.minMappedReadLength - 1 || 0; // make sure to have a delta
          const extendedFileMax: number = bamFileSummary?.maxMappedReadLength + 1 || 0; // make sure to have a delta
          const mixValue: number = normalize(
            // ensure that the readLength-based value never exceeds the min/max range to avoid exceptions
            Math.max(extendedFileMin, Math.min(extendedFileMax, element.readLength)),
            extendedFileMin,
            extendedFileMax,
            0,
            1,
          );
          fill = mix(mixValue, GENOME_TRACK_COLOR.GRADIENT_STOP, GENOME_TRACK_COLOR.GRADIENT_START);
          break;
      }

      // Draw the read part arrow
      return renderArrow({
        start: segment.start,
        stop: segment.stop,
        direction: arrowDirection,
        y,
        height,
        fill,
        hasStroke,
        opacity: 0.7,
      });
    };

    // This is needed as insertions and deletions are send out of segments array
    // @see GraphQL: query getNgsPileupFeatures (in queries.ts)
    // const operationCount = {
    //   insertion: 0,
    //   deletion: 0,
    // };

    // If segments are empty, draw a thick line if it travels the visible range.
    if (segments.length === 0) {
      // No segments to draw but probably a junction between segments
      if (element.start <= range.start && element.end >= range.stop) {
        renderLine({
          start: element.start,
          stop: element.end,
          thickness: 2,
          y,
          fill: GENOME_TRACK_COLOR.GREY,
          height,
        });
      }
    } else {
      segments.forEach((segment, index) => {
        // In Processor all these operations are treated the same way. CIGAR code: M, X, =, EQ
        if (segment.match) {
          // *** Matches or mismatches ***
          const mismatchesBases = segment.mismatchesBases;
          const mismatchesPositions = segment.mismatchPositions;

          // Draw segment shape
          const shapeInteractivity = drawSegment(segment, index).interactive();

          // Draw mismatches
          if (
            Array.isArray(mismatchesBases) &&
            Array.isArray(mismatchesPositions) &&
            mismatchesBases.length === mismatchesPositions.length &&
            // show the mismatches only if there is no specific coloring in place
            coloringMode === NGS_READS_PILEUP_COLORING.DEFAULT
          ) {
            const mismatchesQuantity = mismatchesPositions.length;

            for (let i = 0; i < mismatchesQuantity; i += 1) {
              const letter = mismatchesBases[i];
              const letterPosition = mismatchesPositions[i];

              if (letter != undefined) {
                const upperCaseLetter = letter.toUpperCase();
                const letterShape: GenomeViewerShape = drawLetterShape(
                  segment.start + letterPosition,
                  y,
                  upperCaseLetter,
                  SEQ_RESIDUE_NU_COLORS[upperCaseLetter.toLowerCase()] || SEQ_RESIDUE_NU_COLORS.n,
                );
                const { interactiveShape, tooltipTargetShape } = letterShape.interactive();
                callTooltip(interactiveShape, tooltipTargetShape, {
                  uid: nanoid(),
                  title: element?.readName,
                  type: TRACK_TOOLTIP_TYPE.NGS,
                  getContent: () => loadNgsReadTooltip(element, segment, i),
                });
              }
            }
          }

          // (Mis)Matches Tooltip (Block)
          // const { shape } = renderTransparentRect(tooltipStart, tooltipStop, row);

          callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
            uid: nanoid(),
            title: element?.readName,
            type: TRACK_TOOLTIP_TYPE.NGS,
            getContent: () => loadNgsReadTooltip(element, segment, null),
          });
          segmentShapeInteractivity = shapeInteractivity;
        } else if (segment.deletion) {
          // *** Deletions CIGAR: D ***
          const shape: GenomeViewerShape = renderLine({
            start: segment.start,
            stop: segment.stop,
            thickness: 3,
            y,
            fill: GENOME_TRACK_COLOR.DARK,
            height,
          });

          // Deletions tooltip
          // const { shape } = renderNucTransparentRect(tooltipStart, tooltipStop, row);

          // Because closure
          // const deletionIndex = operationCount.deletion;

          if (shape != undefined) {
            // shape is not rendered because it is too narrow
            const shapeInteractivity = shape.interactive();

            callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
              uid: nanoid(),
              title: element?.readName,
              type: TRACK_TOOLTIP_TYPE.NGS,
              getContent: () => loadNgsReadTooltip(element, segment, null),
            });
          }

          // operationCount.deletion += 1;
        } else if (segment.insertion) {
          // *** Insertions, CIGAR: I ***
          // Draw "little I", no letters at all
          const shape: GenomeViewerShape = renderInsertion({
            start: segment.start,
            y,
            fill: GENOME_TRACK_COLOR.DARK,
            height,
          });
          const shapeInteractivity = shape.interactive();

          // Because closure
          // const insertionIndex = operationCount.insertion;

          callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
            uid: nanoid(),
            title: element?.readName,
            type: TRACK_TOOLTIP_TYPE.NGS,
            getContent: () => loadNgsReadTooltip(element, segment, null),
          });
          // operationCount.insertion += 1;

          // As we are not going to split the reference sequence, we don't move the drawing operation further
          // segmentStart -= operationLength;
        }

        // Gap: Draw thick line between segments
        if (lastStop > 0 && lastStop < segment.start) {
          renderLine({
            start: lastStop,
            stop: segment.start,
            thickness: 2,
            y,
            fill: GENOME_TRACK_COLOR.GREY,
            height,
          });
        }

        lastStop = segment.stop;
      });
    }

    // This is a pair. Draw thin line between parts (first / second)
    if (!isFirst && first != undefined && second.start > first.end) {
      renderLine({
        start: first.end,
        stop: second.start,
        thickness: 1,
        y,
        fill: GENOME_TRACK_COLOR.CLIPPING,
        height,
      });
    }
    return segmentShapeInteractivity;
  }
};

/// --- Export --- ///
const MemoNgsReadTrack = memo(NgsReadTrack, shallowEqual);
export { MemoNgsReadTrack as NgsReadTrack };
